import { useState, useRef } from "react";
import { useDraggable } from "@dnd-kit/core";
import type { EventSchoolPublic } from "@dugads/shared";
import { SCHOOL_COLORS, type DraggableTeam } from "@/lib/scheduler-utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, PanelLeftClose, PanelLeftOpen, Eye, EyeOff, X } from "lucide-react";

interface TeamPanelProps {
  sortedSchools: EventSchoolPublic[];
  schoolTeams: Record<string, DraggableTeam[]>;
  teamMatchCounts: Map<string, number>;
  onAddExtTeam: (schoolName: string, teamName: string) => void;
  onRenameExtTeam: (schoolName: string, oldName: string, newName: string) => void;
  onDeleteExtTeam: (schoolName: string, teamName: string) => void;
}

// 184: a team pill as a dnd-kit draggable. Drag onto a grid cell to schedule
// the team (works on touch via the shared Touch sensor: long-press to lift, so a
// swipe still scrolls the panel). Double-click still renames an external team.
function DraggableTeamPill({ team, colorClasses, allocated, count, isExternal, onStartRename, onDelete }: {
  team: DraggableTeam;
  colorClasses: string;
  allocated: boolean;
  count: number;
  isExternal: boolean;
  onStartRename: () => void;
  onDelete: () => void;
}) {
  const drag = useDraggable({ id: `team-${team.id}`, data: { kind: "team", team, label: team.name } });
  return (
    <div
      ref={drag.setNodeRef}
      {...drag.listeners}
      {...drag.attributes}
      data-item="team" data-item-id={team.id}
      onDoubleClick={isExternal ? onStartRename : undefined}
      className={`group cursor-grab rounded border px-2 py-1.5 text-xs font-medium active:cursor-grabbing ${colorClasses} ${allocated ? "opacity-50" : ""} ${drag.isDragging ? "opacity-40" : ""} flex items-center justify-between`}
      title={isExternal ? "Double-click to rename" : undefined}
    >
      <span>
        {team.name}
        {allocated && <span className="ml-1 font-normal opacity-70">({count})</span>}
      </span>
      {isExternal && !allocated && (
        <button type="button" title="Remove team" onClick={(e) => { e.stopPropagation(); e.preventDefault(); onDelete(); }}
          className="invisible ml-1 rounded p-0.5 hover:bg-black/10 group-hover:visible">
          <X className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}

export function TeamPanel({ sortedSchools, schoolTeams, teamMatchCounts, onAddExtTeam, onRenameExtTeam, onDeleteExtTeam }: TeamPanelProps) {
  const [open, setOpen] = useState(true);
  const [showAllocated, setShowAllocated] = useState(false);
  const [editingTeam, setEditingTeam] = useState<{ schoolName: string; teamName: string } | null>(null);
  const editRef = useRef<HTMLInputElement>(null);
  const [extInputs, setExtInputs] = useState<Record<string, string>>({});

  function getCount(team: DraggableTeam): number {
    // Check by teamId first (Dugads teams), then by composite key or name (external teams)
    if (team.teamId) return teamMatchCounts.get(team.teamId) ?? 0;
    return teamMatchCounts.get(`${team.schoolName} ${team.name}`) ?? teamMatchCounts.get(team.name) ?? 0;
  }

  function submitExtTeam(schoolName: string) {
    const input = extInputs[schoolName]?.trim();
    if (!input) return;
    onAddExtTeam(schoolName, input);
    setExtInputs((prev) => ({ ...prev, [schoolName]: "" }));
  }

  if (!open) {
    return (
      <div className="flex w-8 flex-shrink-0 flex-col items-center border-r pt-3" data-region="team-panel">
        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setOpen(true)} title="Show teams" data-action="toggle-panel">
          <PanelLeftOpen className="h-3.5 w-3.5" />
        </Button>
      </div>
    );
  }

  return (
    <div className="w-52 flex-shrink-0 overflow-y-auto border-r bg-muted/30 p-3" data-region="team-panel">
      <div className="mb-3 flex items-center justify-between">
        <span className="text-xs font-semibold uppercase text-muted-foreground">Teams</span>
        <div className="flex gap-0.5">
          <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setShowAllocated(!showAllocated)}
            title={showAllocated ? "Hide allocated" : "Show all"} data-action="toggle-allocated">
            {showAllocated ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
          </Button>
          <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setOpen(false)} title="Collapse" data-action="toggle-panel">
            <PanelLeftClose className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
      {sortedSchools.map((s, si) => {
        const c = SCHOOL_COLORS[si % SCHOOL_COLORS.length];
        const allTeams = schoolTeams[s.name] ?? [];
        const teams = showAllocated ? allTeams : allTeams.filter((t) => getCount(t) === 0);
        const hiddenCount = allTeams.length - teams.length;
        return (
          <div key={s.id} className="mb-4">
            <div className={`mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase ${c.text}`}>
              <span className={`inline-block h-2.5 w-2.5 rounded-full border ${c.bg} ${c.border}`} />
              <span className="truncate">{s.name}</span>
              {s.is_host && <span className="text-[10px] font-normal normal-case opacity-60">(Host)</span>}
            </div>
            <div className="flex flex-col gap-1">
              {teams.map((team) => {
                const count = getCount(team);
                const allocated = count > 0;
                const isEditing = editingTeam?.schoolName === s.name && editingTeam?.teamName === team.name;
                const isExternal = !team.teamId;

                if (isEditing) {
                  return (
                    <input key={team.id} ref={editRef} defaultValue={team.name}
                      title="Rename team" placeholder="Team name"
                      className={`rounded border px-2 py-1.5 text-xs font-medium outline-none ring-1 ring-primary ${c.bg} ${c.border} ${c.text}`}
                      autoFocus
                      onBlur={(e) => {
                        const v = e.target.value.trim();
                        if (v && v !== team.name) onRenameExtTeam(s.name, team.name, v);
                        setEditingTeam(null);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") { e.preventDefault(); (e.target as HTMLInputElement).blur(); }
                        if (e.key === "Escape") setEditingTeam(null);
                      }}
                    />
                  );
                }

                return (
                  <DraggableTeamPill
                    key={team.id}
                    team={team}
                    colorClasses={`${c.bg} ${c.border} ${c.text}`}
                    allocated={allocated}
                    count={count}
                    isExternal={isExternal}
                    onStartRename={() => setEditingTeam({ schoolName: s.name, teamName: team.name })}
                    onDelete={() => onDeleteExtTeam(s.name, team.name)}
                  />
                );
              })}
              {!showAllocated && hiddenCount > 0 && (
                <button type="button" onClick={() => setShowAllocated(true)}
                  className="text-left text-[11px] text-muted-foreground hover:text-foreground">
                  +{hiddenCount} allocated
                </button>
              )}
              <div className="mt-1 flex gap-1">
                <Input value={extInputs[s.name] ?? ""} onChange={(e) => setExtInputs((prev) => ({ ...prev, [s.name]: e.target.value }))}
                  placeholder="Add team..." className="h-7 text-xs" data-field="ext-team-name"
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submitExtTeam(s.name); } }}
                />
                <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0" title="Add team" data-action="add-ext-team" onClick={() => submitExtTeam(s.name)}>
                  <Plus className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
